/* eslint-disable no-bitwise */

import {
  AppShape,
  BorrowerEntity,
  LoanApplicationBorrowers,
  LoanApplicationBorrower,
  PreApprovalBorrower,
  PreApprovalBorrowers,
} from "../types/api";
import { MultiSelectEvent, MultiSelectSelectedItems } from "../types/forms";

import { IPreApproval } from "../../borrower/web_modules/Signup/interfaces";

interface IBorrowerEntity {
  with(flag: BorrowerEntity): BorrowerEntityUtil;
  without(flag: BorrowerEntity): BorrowerEntityUtil;
  has(flag: BorrowerEntity): boolean;
}

export default class BorrowerEntityUtil implements IBorrowerEntity {
  private borrowerEntity: BorrowerEntity;
  // Allow for a default parameter of 0 for easy chaining
  constructor(borrower: BorrowerEntity = 0) {
    this.borrowerEntity = borrower;
  }

  public with(flag: BorrowerEntity | string | number): BorrowerEntityUtil {
    this.borrowerEntity = this.borrowerEntity | Number(flag);
    return this;
  }

  public without(flag: BorrowerEntity | string | number): BorrowerEntityUtil {
    this.borrowerEntity = this.borrowerEntity & ~Number(flag);
    return this;
  }

  public has(flag: BorrowerEntity | string | number): boolean {
    return (this.borrowerEntity & Number(flag)) !== 0;
  }

  public value(): BorrowerEntity {
    return this.borrowerEntity;
  }

  /**
   * decomposed takes a BorrowerEntityUtil and decomposes it into the individual BorrowerEntity values
   * contained in that BorrowerEntity.
   * ex.
   * @param entity
   */
  public decomposed(entity: BorrowerEntityUtil): BorrowerEntity[] {
    let decomposed: BorrowerEntity[] = [];
    Object.values(BorrowerEntity).forEach((be: BorrowerEntity) => {
      if (entity.has(be)) {
        decomposed = [...decomposed, be];
      }
    });
    return decomposed;
  }

  /**
   * Testing/Debugging Functions
   * Functions below are primarily for testing
   */

  // Converts the current borrower_entity to the corresponding bit array as a string (without leading zeros)
  // eg: 3 -> 0011
  public toBitArrayString(): string {
    return this.borrowerEntity.toString(2);
  }

  // Converts the current borrower_entity to the corresponding bit array as a number[] (without leading zeros)
  // eg: 3 -> 0011 -> [0,0,1,1]
  public toBitArray(): number[] {
    return this.toBitArrayString().split("").map(Number);
  }

  // Returns a new BorrowerEntity based off another BorrowerEntity
  static clone(be: BorrowerEntityUtil): BorrowerEntityUtil {
    return new BorrowerEntityUtil(be.value());
  }
}

// Returns the primary borrower as an object
export const primaryBorrower = (app: AppShape): LoanApplicationBorrower =>
  app.borrowers[app.primary_borrower_entity];

// For a given BorrowerEntity value, returns the corresponding borrowers from the app
export const getAssociatedBorrowers = (
  borrowerEntity: number | BorrowerEntity,
  borrowers: LoanApplicationBorrowers,
): LoanApplicationBorrower[] => {
  const bitmask = new BorrowerEntityUtil(borrowerEntity);

  return Object.keys(borrowers)
    .map(be => borrowers[be])
    .filter((be: LoanApplicationBorrower) => bitmask.has(be.borrower_entity));
};

/**
 * getBorrowerEntitiesAsStrings takes in an array of LoanApplicationBorrowers and returns their entities
 * in a human-readable format, e.g. ["Borrower 1", "Borrower 2", "Borrower 3", "Borrower 4"]
 * @param associatedBorrowers
 */
export const getBorrowerEntitiesAsStrings = (
  associatedBorrowers: LoanApplicationBorrower[],
): string[] => {
  return associatedBorrowers.map((borrower: LoanApplicationBorrower) => {
    switch (borrower.borrower_entity) {
      case 1:
        return "Borrower 1";
      case 2:
        return "Borrower 2";
      case 4:
        return "Borrower 3";
      case 8:
        return "Borrower 4";
      default:
        return "None";
    }
  });
};

/**
 * getBorrowerEntityAsString takes in the borrower entity and returns it
 * in a human-readable format, e.g. "Borrower 1", "Borrower 2"
 * @param borrowerEntity
 */
export const getBorrowerEntityAsString = (borrowerEntity: BorrowerEntity): string => {
  switch (borrowerEntity) {
    case 1:
      return "Borrower 1";
    case 2:
      return "Borrower 2";
    case 4:
      return "Borrower 3";
    case 8:
      return "Borrower 4";
    default:
      return "None";
  }
};

/** Given an app.borrowers, returns borrower options. Compatible with MultiSelect's items prop.
 * @param  {LoanApplicationBorrowers} borrowers
 */
export const borrowerEntitySelectOptions = (
  borrowers: LoanApplicationBorrowers,
): MultiSelectSelectedItems[] => {
  return Object.keys(borrowers).map(borrowerEntity => {
    const borrower: LoanApplicationBorrower = borrowers[borrowerEntity];
    const { id, first_name, last_name, borrower_entity } = borrower;

    return {
      id: `${id}`, // reminder: borrower.id is different from borrower_entity
      label: `${first_name} ${last_name}`,
      value: borrower_entity,
    };
  });
};

/** Given an AppShape, returns borrower options. Compatible with MultiSelect's items prop.
 * @param  {AppShape} app
 */
export const borrowerEntitySelectOptionsFromApp = (app: AppShape): MultiSelectSelectedItems[] => {
  return borrowerEntitySelectOptions(app.borrowers);
};

/** Given a LoanApplicationBorrowers map, returns number of borrowers
 * @param  {IPreApproval|AppShape} appOrPreapp
 */
export const borrowerCount = (borrowers: LoanApplicationBorrowers | PreApprovalBorrowers): number =>
  Object.keys(borrowers).length;

/** Given a PreApproval or AppShape, returns number of borrowers
 * @param  {IPreApproval|AppShape} appOrPreapp
 */
export const borrowerCountFromApp = (appOrPreapp: IPreApproval | AppShape): number => {
  if (!appOrPreapp.borrowers) {
    return 0;
  }
  return Object.keys(appOrPreapp.borrowers).length;
};

/** Given a PreApproval or AppShape, returns the borrower entity of the last borrower added
 * @param  {IPreApproval|AppShape} app
 */
export const mostRecentlyAddedBorrowerEntity = (appOrPreapp: IPreApproval | AppShape): number => {
  if (!appOrPreapp.borrowers) {
    return 0;
  }

  // Suppose a borrower with borrower entities 1, 2, 4. This would be 4.
  const highestExistingEntity = Object.keys(appOrPreapp.borrowers)
    .map(Number)
    .sort((a, b) => b - a)[0]; // Grab the first after sorting by descending, giving us the highest borrower entity

  return highestExistingEntity;
};

// Conditional TS Type computes return type based on the type of Borrower passed in
type BorrowerLike = PreApprovalBorrowers | LoanApplicationBorrowers;
type LoanAppOrPreAppBorrower<T extends BorrowerLike> = [T] extends [LoanApplicationBorrowers]
  ? LoanApplicationBorrower
  : PreApprovalBorrower;

/** Given a borrowers object, returns the borrowers in ascending borrower entity order (1, 2, 4, 8, etc)
 * @param  {LoanApplicationBorrowers} app
 */
export const borrowersAscending = <T extends BorrowerLike>(
  borrowers: T,
): LoanAppOrPreAppBorrower<T>[] => {
  // Sort by ascending borrower entity
  const borrowersAsc: LoanAppOrPreAppBorrower<T>[] = Object.keys(borrowers)
    .map((borrowerEntity: string) => borrowers[borrowerEntity])
    .sort((a, b) => Number(a) - Number(b));

  return borrowersAsc;
};

/** Given a preApproval (or AppShape), returns the next available borrowerEntity (1, 2, 4, 8, etc)
 * @param  {IPreApproval | AppShape} app
 */
export const nextBorrowerEntityFromApp = (appOrPreapp: IPreApproval | AppShape): BorrowerEntity => {
  if (!appOrPreapp.borrowers) {
    return new BorrowerEntityUtil(1).value();
  }

  const highestExistingEntity = mostRecentlyAddedBorrowerEntity(appOrPreapp);
  const nextExponent = new BorrowerEntityUtil(highestExistingEntity).toBitArrayString().length;
  return new BorrowerEntityUtil(2 ** nextExponent).value();
};

/** Given a number, returns the next available borrowerEntity (2, 4, 8, etc)
 * @param  {IPreApproval | AppShape} app
 */
export const nextBorrowerEntity = (number: BorrowerEntity): BorrowerEntity => {
  if (number === 0) {
    return new BorrowerEntityUtil(1).value();
  }

  const nextExponent = new BorrowerEntityUtil(number).toBitArrayString().length;
  return new BorrowerEntityUtil(2 ** nextExponent).value();
};

/** Given an preApproval, returns a preApproval with the borrower added
 * @param  {IPreApproval} preApproval
 * @param  {LoanApplicationBorrower} borrower
 */
export const addBorrowerToPreApproval = (
  preApproval: IPreApproval,
  borrower: Partial<PreApprovalBorrower>,
): IPreApproval => {
  const nextEntity = nextBorrowerEntityFromApp(preApproval);
  const updatedBorrower = {
    ...borrower,
    borrower_entity: nextEntity,
  };

  const borrowers = preApproval.borrowers
    ? {
        ...preApproval.borrowers, // Spread existing borrowers object if exists
        [nextEntity]: updatedBorrower,
      }
    : {
        [nextEntity]: updatedBorrower,
      };

  return {
    ...preApproval,
    borrowers,
  };
};

/** Given a LoanApplicationBorrowers map, returns an updated map with the borrower updated
 * @param  {LoanApplicationBorrowers} borrowers
 * @param  {BorrowerEntity} borrowerEntity
 * @param  {LoanApplicationBorrower} updatedBorrowerData
 */
export const updateBorrowerMap = (
  borrowers: PreApprovalBorrowers,
  borrowerEntity: BorrowerEntity | number,
  updatedBorrowerData: Partial<PreApprovalBorrower>,
): PreApprovalBorrowers => {
  if (!borrowers?.[borrowerEntity]) {
    throw new Error("attempted to update a borrower that does not exist");
  }
  const currentBorrowerData = borrowers[borrowerEntity];
  return {
    ...borrowers,
    [borrowerEntity]: {
      ...currentBorrowerData,
      ...updatedBorrowerData,
    },
  };
};

/** Given an preApproval, returns a preApproval with the borrower updated
 * @param  {IPreApproval} preApproval
 * @param  {BorrowerEntity} borrowerEntity
 * @param  {LoanApplicationBorrower} updatedBorrowerData
 */
export const updateBorrowerOnPreApproval = (
  preApproval: IPreApproval,
  borrowerEntity: BorrowerEntity,
  updatedBorrowerData: Partial<PreApprovalBorrower>,
): IPreApproval => {
  if (!preApproval?.borrowers?.[borrowerEntity]) {
    throw new Error("attempted to update a borrower that does not exist");
  }
  return {
    ...preApproval,
    borrowers: updateBorrowerMap(preApproval.borrowers, borrowerEntity, updatedBorrowerData),
  };
};

/** To be used in a Borrower Entity generic multiple-select component's onChange prop to handle the event
 * Converts individual entities into the bitmask version of the borrower_entity
 * @param  {any} event
 */
export const genericSelectedBorrowersToBorrowerEntity = <T>(
  selectedItems: T[],
): BorrowerEntityUtil => {
  if (selectedItems.length === 0) return new BorrowerEntityUtil(0);

  return selectedItems.reduce((acc, curr: T | any) => {
    return acc.with(curr.value);
  }, new BorrowerEntityUtil(0));
};

/** To be used in a Borrower Entity MultiSelect's onChange prop to handle the event
 * Converts individual entities into the bitmask version of the borrower_entity
 * @param  {MultiSelectEvent} event
 */
export const carbonSelectedBorrowersToBorrowerEntity = (
  event: MultiSelectEvent,
): BorrowerEntityUtil => {
  const { selectedItems } = event;

  if (selectedItems.length === 0) return new BorrowerEntityUtil(0);

  return selectedItems.reduce((acc, curr) => {
    return acc.with(curr.value);
  }, new BorrowerEntityUtil(0));
};

/**
 * Check if all coborrowers have verified their emails
 * (confirmed_at field is present)
 * @param primary_borrower_entity
 * @param borrowers Borrowers map
 * @returns
 */
export const allCoBorrowersEmailsVerified = (
  primary_borrower_entity: BorrowerEntity,
  borrowers: LoanApplicationBorrowers,
): boolean => {
  return borrowersAscending(borrowers)
    .filter(b => b.borrower_entity !== primary_borrower_entity)
    .every(b => b.confirmed_at);
};

/**
 * Given an application, return all borrowers names formatted and as
 * a csv
 * @param app
 */
export const getBorrowersFormattedNames = (app: AppShape): string => {
  const borrowerNames = Object.keys(app.borrowers).map(be => {
    const borrower: LoanApplicationBorrower = app.borrowers[be];
    return `${borrower.first_name} ${borrower.last_name}`;
  });
  return borrowerNames.join(", ");
};

export const getNextCoborrower = (
  borrowers: LoanApplicationBorrowers,
  primary_borrower_entity: BorrowerEntity,
  currentBorrowerEntity: BorrowerEntity,
): LoanApplicationBorrower | null => {
  let prevIndex = -1;
  const coborrowerArray = borrowersAscending(borrowers).filter(
    b => b.borrower_entity !== primary_borrower_entity,
  );
  if (currentBorrowerEntity !== primary_borrower_entity) {
    prevIndex = coborrowerArray.findIndex(b => b.borrower_entity === currentBorrowerEntity);
  }
  const nextCoborrower =
    prevIndex < coborrowerArray.length - 1 ? coborrowerArray[prevIndex + 1] : null;
  return nextCoborrower;
};
